/**
 * The YubiKey pool contract.
 *
 * Unlike a terminal, a key is not scoped to a location: `yubikeys.location_id` is nullable, and a
 * key legitimately exists unassigned in a warehouse before it is ever installed anywhere. That is
 * why this is its own top-level admin surface rather than a panel nested under one location's
 * detail view the way terminals are — "how many keys do we own and what state are they in" is a
 * fleet-wide question a per-location screen cannot answer.
 *
 * `status` and `type` are `text` columns in the database with a Drizzle-level `enum` option that
 * Postgres itself does not enforce (the schema's own comment says so). The two closed vocabularies
 * below are declared once here and are what every write actually validates against; the database
 * column staying unconstrained text is an inherited fact this contract does not need to change to
 * be strict on the way in.
 */
import { z } from 'zod';
import { nonEmptyUpdate } from './content/common.js';
export const yubikeyStatuses = ['available', 'installed', 'not_installed', 'damaged'];
export const yubikeyStatusSchema = z.enum(yubikeyStatuses);
export const yubikeyTypes = ['None', 'OTC', 'Kiosk'];
export const yubikeyTypeSchema = z.enum(yubikeyTypes);
const yubikeyEditableShape = {
    serialNumber: z.string().min(1),
    locationId: z.number().int().positive().nullable(),
    status: yubikeyStatusSchema,
    type: yubikeyTypeSchema,
    notes: z.string().min(1).nullable(),
};
export const yubikeySchema = z.object({
    id: z.number().int().positive(),
    ...yubikeyEditableShape,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const yubikeyListSchema = z.array(yubikeySchema);
export const createYubikeySchema = z.object(yubikeyEditableShape).extend({
    locationId: yubikeyEditableShape.locationId.optional(),
    status: yubikeyEditableShape.status.optional(),
    type: yubikeyEditableShape.type.optional(),
    notes: yubikeyEditableShape.notes.optional(),
});
export const updateYubikeySchema = nonEmptyUpdate(z.object(yubikeyEditableShape));
//# sourceMappingURL=yubikeys.js.map